import DeleteIcon from "@mui/icons-material/Delete";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { Box, Chip, IconButton, Tooltip, useTheme } from "@mui/material";
import { useCallback } from "react";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { MediaSegmentFE } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { useScriptEditor } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import {
  getSegmentErrors,
  type ScriptValidationError,
} from "Projects/Utils/validateScript";

interface MediaSegmentNodeProps {
  index: number;
  segment: MediaSegmentFE;
  /** Map from partnerMediaFileID → display name for label */
  mediaFileNames?: Record<string, string>;
  validationErrors?: ScriptValidationError[];
}

export const MediaSegmentNode = ({
  index,
  segment,
  mediaFileNames,
  validationErrors = [],
}: MediaSegmentNodeProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { removeSegment, selectedSegmentIndex, focusSegment, isEditable } =
    useScriptEditor();

  const isSelected = selectedSegmentIndex === index;
  const segErrors = getSegmentErrors(validationErrors, index);
  const hasError = segErrors.length > 0 || !segment.partnerMediaFileID;

  const label = segment.partnerMediaFileID
    ? (mediaFileNames?.[segment.partnerMediaFileID] ??
      segment.partnerMediaFileID)
    : "—";

  const errorTooltip = segErrors.some((e) => e.type === "invalidStartTime")
    ? t(Resources.JobDetail.SegmentInvalidStartTime)
    : undefined;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      focusSegment(index);
    },
    [index, focusSegment],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeSegment(index);
    },
    [index, removeSegment],
  );

  const chipColor = hasError
    ? theme.palette.error.main
    : theme.palette.success.main;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        position: "relative",
        m: 0.5,
        verticalAlign: "middle",
        "&:hover .media-delete-btn": { opacity: 1 },
      }}
    >
      <Tooltip title={errorTooltip} placement="top">
        <Chip
          icon={<MusicNoteIcon sx={{ fontSize: 14 }} />}
          label={label}
          size="small"
          sx={{
            fontSize: "12px",
            height: 26,
            maxWidth: "180px",
            backgroundColor: isSelected
              ? `${theme.palette.success.main}33`
              : `${theme.palette.success.main}22`,
            color: chipColor,
            border: `1px solid ${
              hasError
                ? theme.palette.error.main
                : isSelected
                  ? theme.palette.success.main
                  : `${theme.palette.success.main}66`
            }`,
            cursor: "pointer",
            "& .MuiChip-icon": { color: chipColor },
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            transition: "background-color 150ms, border-color 150ms",
            "&:hover": {
              backgroundColor: `${theme.palette.success.main}33`,
            },
          }}
        />
      </Tooltip>
      {isEditable && (
        <Tooltip title={t(Resources.JobDetail.DeleteMedia)} placement="top">
          <IconButton
            className="media-delete-btn"
            size="small"
            onClick={handleRemove}
            sx={{
              ml: 0.25,
              opacity: 0,
              p: 0.25,
              color: "text.secondary",
              transition: "opacity 150ms",
              "&:hover": { color: "error.main" },
            }}
          >
            <DeleteIcon sx={{ fontSize: 11 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
